import { z } from 'zod';

// Test mobile numbers that should never receive real SMS
export const TEST_MOBILE_NUMBER = {
  AU_0400_000_000: '+61400000000',
  AU_0477_123_456: '+61477123456',
  AU_0488_123_456: '+61488123456',
  AU_0499_123_456: '+61499123456',
} as const;
export const TEST_MOBILE_NUMBERS: readonly string[] = Object.values(TEST_MOBILE_NUMBER);

/** `sub` of the seeded test-cafe customer (seed/transforms/withBase.ts); the dev login bypass mints it. */
export const TEST_RESTAURANT_CUSTOMER_SUB = 'dev-restaurant-customer';

export const STAGES = ['dev', 'staging', 'production'] as const;
export const zStage = z.literal(STAGES);
export type Stage = z.output<typeof zStage>;

/**
 * The apps that send logs to PostHog, stamped on every event as the `app` property. `convex`: the
 * backend's analytics events (`posthog/track.ts`).
 */
export const APP_NAMES = [
  'pos-native',
  'admin-native',
  'consumer-native',
  'admin-web',
  'consumer-web',
  'convex',
] as const;
export const zAppName = z.literal(APP_NAMES);
export type AppName = z.output<typeof zAppName>;

/** The Expo apps: each ships store builds and JS updates (EAS Update), gated by `appUpdates`. */
export const NATIVE_APP_NAMES = [
  'pos-native',
  'admin-native',
  'consumer-native',
] as const satisfies readonly AppName[];
export const zNativeAppName = z.literal(NATIVE_APP_NAMES);
export type NativeAppName = z.output<typeof zNativeAppName>;

/** The web apps: a browser loads their latest web deploy on every page load, gated by `appUpdates`. */
export const WEB_APP_NAMES = ['admin-web', 'consumer-web', 'pos-docket-viewer'] as const;

/** Every app whose running code `appUpdates` gates: the Expo apps and the web apps. */
export const CLIENT_APP_NAMES = [...NATIVE_APP_NAMES, ...WEB_APP_NAMES] as const;
export const zClientAppName = z.literal(CLIENT_APP_NAMES);
export type ClientAppName = z.output<typeof zClientAppName>;

export const LOG_LEVELS = ['DEBUG', 'INFO', 'WARN', 'ERROR'] as const;
export const zLogLevel = z.literal(LOG_LEVELS);
export type LogLevel = z.output<typeof zLogLevel>;

// Linkly Response Types
// https://www.linkly.com.au/apidoc/REST/#apiresponse
// Note: `errorRecovery` is a non-Linkly response type added for logging/debugging of Linkly 202/404/408/500 error responses.
export const LINKLY_RESPONSE_TYPES = [
  'transaction',
  'logon',
  'settlement',
  'status',
  'querycard',
  'configuremerchant',
  'reprintreceipt',
  'display',
  'receipt',
  'errorRecovery',
] as const;
export const zLinklyResponseType = z.literal(LINKLY_RESPONSE_TYPES);
export type LinklyResponseType = z.output<typeof zLinklyResponseType>;

export const LINKLY_TEST_CASES = ['408_RESPONSE', '500_RESPONSE'] as const;
export const zLinklyTestCase = z.literal(LINKLY_TEST_CASES);
export type LinklyTestCase = z.output<typeof zLinklyTestCase>;

export const LINKLY_RESULTS = [
  'APPROVED',
  'DECLINED',
  'OPERATOR_CANCELLED',
  'OPERATOR_TIMEOUT',
  'SERVER_TIMEOUT',
  'PINPAD_BUSY',
  'PINPAD_OFFLINE',
  'SEND_REQUEST_FAILED',
  'RESPONSE_PARSE_ERROR',
  'UNAUTHORIZED',
  'NOT_FOUND',
] as const;
export const zLinklyResult = z.literal(LINKLY_RESULTS);
export type LinklyResult = z.output<typeof zLinklyResult>;

export const LINKLY_REQUEST_TYPES = ['transaction', 'status', 'sendkey'] as const;
export const zLinklyRequestType = z.literal(LINKLY_REQUEST_TYPES);
export type LinklyRequestType = z.output<typeof zLinklyRequestType>;

// Linkly Transaction Types: P = Purchase, R = Refund
export const LINKLY_TXN_TYPES = ['P', 'R'] as const;
export const zLinklyTxnType = z.literal(LINKLY_TXN_TYPES);
export type LinklyTxnType = z.output<typeof zLinklyTxnType>;

// Send Key values: https://www.linkly.com.au/apidoc/REST/#send-key
// CANCEL/OK = '0', YES = '1', NO = '2', AUTH = '3'
export const LINKLY_KEYS = ['CANCEL', 'OK', 'YES', 'NO', 'AUTH'] as const;
export const zLinklyKey = z.literal(LINKLY_KEYS);
export type LinklyKey = z.output<typeof zLinklyKey>;

// Linkly Transaction State
export const LINKLY_TRANSACTION_STATES = [
  'CREATED',
  'REQUEST_SENT',
  'PROCESSING',
  'COMPLETED',
  'ERROR',
  'TIMEOUT',
  'RECOVERED',
] as const;
export const zLinklyTransactionState = z.literal(LINKLY_TRANSACTION_STATES);
export type LinklyTransactionState = z.output<typeof zLinklyTransactionState>;

// Linkly Receipt Types: C = Customer, M = Merchant, S = Settlement
export const LINKLY_RECEIPT_TYPES = ['C', 'M', 'S'] as const;
export const zLinklyReceiptType = z.literal(LINKLY_RECEIPT_TYPES);
export type LinklyReceiptType = z.output<typeof zLinklyReceiptType>;

// Linkly Connection Status
export const LINKLY_CONNECTION_STATUSES = [
  'NOT_PAIRED',
  'PAIRED',
  'UNAUTHORIZED',
  'OFFLINE',
] as const;
export const zLinklyConnectionStatus = z.literal(LINKLY_CONNECTION_STATUSES);
export type LinklyConnectionStatus = z.output<typeof zLinklyConnectionStatus>;

// Where a Linkly device routes its API calls. Hosts only — `sandbox` always means Linkly's Windows
// VPP (virtual pinpad), `production` always means real hardware.
// - `emulator`   → OrderHub's Convex fake of Linkly's Cloud API (no real device, no card).
// - `sandbox`    → Linkly sandbox hosts (paired to Linkly's Windows VPP; no real cards).
// - `production` → Linkly production hosts (paired to a real physical terminal; real cards).
export const LINKLY_ENVS = ['emulator', 'sandbox', 'production'] as const;
export const zLinklyEnv = z.literal(LINKLY_ENVS);
export type LinklyEnv = z.output<typeof zLinklyEnv>;

export const OPERATIONAL_STATUSES = ['OPEN', 'CLOSED', 'OFFLINE'] as const;
export const zOperationalStatus = z.literal(OPERATIONAL_STATUSES);
export type OperationalStatus = z.output<typeof zOperationalStatus>;

export const PUSH_NOTIFICATION_PERMISSION_STATUSES = ['granted', 'denied', 'undetermined'] as const;
export const zPushNotificationPermissionStatus = z.literal(PUSH_NOTIFICATION_PERMISSION_STATUSES);
export type PushNotificationPermissionStatus = z.output<typeof zPushNotificationPermissionStatus>;

// Payment Type
export const PAYMENT_TYPES = ['CASH', 'EFTPOS', 'CREDIT_CARD'] as const;
export const zPaymentType = z.literal(PAYMENT_TYPES);
export type PaymentType = z.output<typeof zPaymentType>;

// Online Payment Type
export const ONLINE_PAYMENT_TYPES = ['CASH', 'CREDIT_CARD'] as const;
export const zOnlinePaymentType = z.literal(ONLINE_PAYMENT_TYPES) satisfies z.ZodType<
  z.output<typeof zPaymentType>
>;

export const PAYMENT_GATEWAYS = ['STRIPE', 'FAT_ZEBRA', 'LINKLY', 'WORLDLINE', 'SQUARE'] as const;
export const zPaymentGateway = z.literal(PAYMENT_GATEWAYS);
export type PaymentGateway = z.output<typeof zPaymentGateway>;

// Online Payment Gateway (restaurant-level config for which gateway to use for online credit card payments)
// `SQUARE` is never stored: a Square venue's gateway comes from linking Square
// (`integrations/domain.toReceiverOnlineOptions`), so unlinking restores the venue's own.
export const ONLINE_PAYMENT_GATEWAYS = ['STRIPE', 'WORLDLINE', 'SQUARE'] as const;
export const zOnlinePaymentGateway = z.literal(ONLINE_PAYMENT_GATEWAYS);
export type OnlinePaymentGateway = z.output<typeof zOnlinePaymentGateway>;

// Payment Status (Note: VOIDED is deprecated, use FAILED instead)
export const PAYMENT_STATUSES = [
  'PAID',
  'PENDING',
  'REFUNDED',
  'AUTHORIZED',
  'FAILED',
  'VOIDED',
] as const;
export const zPaymentStatus = z.literal(PAYMENT_STATUSES);
export type PaymentStatus = z.output<typeof zPaymentStatus>;

// Push notification status
export const PUSH_NOTIFICATION_STATUSES = ['pending', 'accepted', 'sent', 'error'] as const;
export const zPushNotificationStatus = z.literal(PUSH_NOTIFICATION_STATUSES);
export type PushNotificationStatus = z.output<typeof zPushNotificationStatus>;

// Modifier Type
export const MODIFIER_TYPES = ['SIZE', 'OPTION'] as const;
export const zModifierType = z.literal(MODIFIER_TYPES);
export type ModifierType = z.output<typeof zModifierType>;

// Day of week - Start on SUNDAY to match JavaScript Date.getDay() method.
export const DAYS_OF_WEEK = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
] as const;
export const zDayOfWeek = z.literal(DAYS_OF_WEEK);
export type DayOfWeek = z.output<typeof zDayOfWeek>;

// Service type
// NONE = store is open but online ordering unavailable
// TABLE = QR code ordering available
export const SERVICE_TYPES = ['NONE', 'DELIVERY', 'PICKUP', 'TABLE'] as const;
export const zServiceType = z.literal(SERVICE_TYPES);
export type ServiceType = z.output<typeof zServiceType>;

// Platform
// AI_PHONE = a customer's call to the venue's AI phone number (`ai-phone-ordering` flag)
export const PLATFORMS = [
  'POS',
  'CONSUMER_IOS',
  'CONSUMER_ANDROID',
  'CONSUMER_WEB',
  'AI_PHONE',
  'ADMIN_IOS',
  'ADMIN_ANDROID',
  'ADMIN_WEB',
] as const;
export const zPlatform = z.literal(PLATFORMS);
export type Platform = z.output<typeof zPlatform>;

// Order Status
export const ORDER_STATUSES = [
  'SUBMITTED',
  'CONFIRMED',
  'PARKED',
  'IN_TRANSIT',
  'CANCELLED',
  'COMPLETED',
] as const;
export const zOrderStatus = z.literal(ORDER_STATUSES);
export type OrderStatus = z.output<typeof zOrderStatus>;

// Cancelled Reason
// UNPAID_AT_REGISTER_CLOSE = zero-paid order left open when the idle register close ran;
// distinguishable in reports from customer cancellations.
export const CANCELLED_REASONS = [
  'FOOD_UNAVAILABLE',
  'TOO_BUSY',
  'PLEASE_CALL',
  'TIMEOUT',
  'USER_CANCELLED',
  'UNPAID_AT_REGISTER_CLOSE',
] as const;
export const zCancelledReason = z.literal(CANCELLED_REASONS);
export type CancelledReason = z.output<typeof zCancelledReason>;

// Register closure type — how the trading session was closed.
// MANUAL = staff ran the End of Day flow and counted the till; UNATTENDED = the primary POS closed
// the register with nobody present (idle outside service hours, or catch-up on launch), so nobody
// counted the till and actuals stay null until entered in admin-web.
export const REGISTER_CLOSURE_TYPES = ['MANUAL', 'UNATTENDED'] as const;
export const zRegisterClosureType = z.literal(REGISTER_CLOSURE_TYPES);
export type RegisterClosureType = z.output<typeof zRegisterClosureType>;

// Network type
export const PRINTER_NETWORKS = [
  'WIFI',
  'WIMAX',
  'CELLULAR',
  'ETHERNET',
  'BLUETOOTH',
  'VPN',
  'NONE',
  'OTHER',
  'UNKNOWN',
] as const;
export const zPrinterNetwork = z.literal(PRINTER_NETWORKS);
export type PrinterNetwork = z.output<typeof zPrinterNetwork>;

// Printer Interface type
export const PRINTER_INTERFACES = [
  'LAN',
  'USB',
  /**
   * The docket viewer printer (`isDocketViewerPrinter`): dockets sent to it show on the phone
   * docket viewer instead of printing on paper. Not a kitchen display (KDS).
   */
  'SCREEN',
] as const;
export const zPrinterInterface = z.literal(PRINTER_INTERFACES);
export type PrinterInterface = z.output<typeof zPrinterInterface>;

// Printer SDK — which native SDK the POS app uses to discover/communicate with a printer.
// Tuple order is the canonical discovery order: SDKs run sequentially in this order.
export const PRINTER_SDKS = ['STAR_MICRONICS', 'EPSON'] as const;
export const zPrinterSdk = z.literal(PRINTER_SDKS);
export type PrinterSdk = z.output<typeof zPrinterSdk>;

// Default SDK(s) searched during printer discovery when a restaurant hasn't configured a selection.
// An empty selection is allowed (no discovery runs); only an unset value falls back to this default.
export const DEFAULT_PRINTER_DISCOVERY_SDKS: PrinterSdk[] = ['STAR_MICRONICS'];

// Print Job Status
export const PRINT_JOB_STATUSES = [
  'QUEUED_FOR_PROCESSING',
  'QUEUED_FOR_PRINTING',
  'PROCESSING_ERROR',
  'PRINTING',
  'DONE',
] as const;
export const zPrintJobStatus = z.literal(PRINT_JOB_STATUSES);
export type PrintJobStatus = z.output<typeof zPrintJobStatus>;

// Print Error Type
export const PRINT_ERROR_TYPES = [
  'PRINTER_OFFLINE',
  'NO_PAPER',
  'COVER_OPEN',
  'CUTTER_ERROR',
  'UNSUPPORTED_MODEL',
  'UNKNOWN',
] as const;
export const zPrintErrorType = z.literal(PRINT_ERROR_TYPES);
export type PrintErrorType = z.output<typeof zPrintErrorType>;

// Docket Type (Note: GENERIC_HTML is deprecated, use specific type instead)
export const DOCKET_TYPES = [
  'KITCHEN_DOCKET',
  'CASH_DRAWER',
  'CUSTOMER_RECEIPT',
  'END_OF_DAY_REPORT',
  'GENERIC_HTML',
] as const;
export const zDocketType = z.literal(DOCKET_TYPES);
export type DocketType = z.output<typeof zDocketType>;

// Docket Status - Don't use 'PRINTING' as status; use printer queue `inFlightDocketUuid` instead
export const DOCKET_STATUSES = ['QUEUED', 'EXPIRED', 'ERROR', 'PRINTED'] as const;
export const zDocketStatus = z.literal(DOCKET_STATUSES);
export type DocketStatus = z.output<typeof zDocketStatus>;

// Notice Type
export const NOTICE_TYPES = ['BANNER', 'MODAL', 'CALLOUT'] as const;
export const zNoticeType = z.literal(NOTICE_TYPES);
export type NoticeType = z.output<typeof zNoticeType>;
